import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SEO } from '@/components/SEO';
import {
  Download,
  Calculator,
  Shield,
  ArrowRight,
  Check,
  Crown,
  TrendingUp,
  Users,
  FileText,
  Share2,
  Percent,
  Plus,
  ImageIcon,
  CreditCard,
  Eye,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { INVOICE_TEMPLATES, getTemplate, type TemplateId } from '@/components/invoice/InvoiceTemplates';
import { useAuth } from '@/hooks/useAuth';
import { AnonymousEditor } from '@/components/AnonymousEditor';

const SHOW_INTERACTIVE_INVOICE = false;

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [homePageVersion, setHomePageVersion] = useState<'v1' | 'v2'>('v2');
  const [stats, setStats] = useState({
    invoicesCount: 10452,
    usersCount: 1256,
    countriesCount: 45,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Check if db is initialized
        if (!db) {
          console.error('Firebase not initialized');
          return;
        }

        // Fetch public stats document
        const statsDoc = await getDocs(collection(db, 'publicStats'));

        if (!statsDoc.empty) {
          const statsData = statsDoc.docs[0].data();
          const useFake = statsData.showFakeStats === true;
          setStats({
            invoicesCount: useFake ? (statsData.fakeInvoicesCount ?? 10452) : (statsData.totalInvoices || 10452),
            usersCount: useFake ? (statsData.fakeUsersCount ?? 1256) : (statsData.totalUsers || 1256),
            countriesCount: useFake ? (statsData.fakeCountriesCount ?? 45) : (statsData.totalCountries || 45),
          });
          if (statsData.homePageVersion === 'v2') {
            setHomePageVersion('v2');
          } else {
            // Keep the focused marketing homepage as the default. The legacy
            // layout is intentionally no longer selected by an unset setting.
            setHomePageVersion('v2');
          }
        } else {
          // Fallback to default values if no stats document exists
          setStats({
            invoicesCount: 10452,
            usersCount: 1256,
            countriesCount: 45,
          });
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Set default values on error
        setStats({
          invoicesCount: 10452,
          usersCount: 1256,
          countriesCount: 45,
        });
      }
    };

    fetchStats();
  }, []);

  // Interactive Showcase States
  const [templateId, setTemplateId] = useState<TemplateId>('ocean');
  const [businessName, setBusinessName] = useState('My Creative Agency');
  const [businessAddress, setBusinessAddress] = useState('1209 Orange Street\nWilmington, DE 19801\nUnited States');
  const [clientName, setClientName] = useState('Acme Corporation');
  const [clientAddress, setClientAddress] = useState('1600 Amphitheatre Pkwy\nMountain View, CA 94043');
  const [invoiceNumber] = useState('INV-2026-001');
  const [items, setItems] = useState([
    { code: 'DEV-01', description: 'Website Design & Development', quantity: 1, rate: 1200, unit: 'hrs' },
    { code: 'SEO-02', description: 'SEO Copywriting & Strategy', quantity: 1, rate: 350, unit: 'pcs' },
  ]);

  // Quotation showcase states
  const [quoteBusinessName, setQuoteBusinessName] = useState('My Creative Agency');
  const [quoteBusinessAddress, setQuoteBusinessAddress] = useState('1209 Orange Street\nWilmington, DE 19801\nUnited States');
  const [quoteClientName, setQuoteClientName] = useState('Wayne Enterprises');
  const [quoteClientAddress, setQuoteClientAddress] = useState('1007 Mountain Drive\nGotham City, NJ 08012');
  const [quoteNumber] = useState('QT-2026-042');
  const [quoteItems, setQuoteItems] = useState([
    { code: 'APP-01', description: 'Mobile App Development (iOS/Android)', quantity: 1, rate: 4500, unit: 'pcs' },
    { code: 'CLD-03', description: 'Cloud Infrastructure & DevOps Setup', quantity: 1, rate: 1500, unit: 'pcs' },
  ]);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const activeTpl = getTemplate(templateId);

  const formatUSD = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  // Compute totals for Invoice (Top Card)
  const subtotal = items.reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.rate) || 0)), 0);
  const tax = subtotal * 0.1; // 10% VAT demo
  const discount = subtotal * 0.05; // 5% Discount demo
  const total = subtotal + tax - discount;

  // Compute totals for Quotation (Bottom Card)
  const quoteSubtotal = quoteItems.reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.rate) || 0)), 0);
  const quoteTax = quoteSubtotal * 0.1; // 10% VAT demo
  const quoteDiscount = quoteSubtotal * 0.05; // 5% Discount demo
  const quoteTotal = quoteSubtotal + quoteTax - quoteDiscount;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'Is QuickFreeInvoice really free?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Yes! QuickFreeInvoice lets you create, save, and download unlimited invoices and quotations for free. Upgrading to Pro unlocks advanced features like saved profiles, priority support, and Word document exports.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Do I need to install any software?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'No installation required! QuickFreeInvoice is a web-based generator that runs in any modern web browser on desktop, tablet, or mobile devices.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Can I customize my invoices?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Yes! Add your business logo, custom line items, global taxes (VAT, GST, SST), discounts, payment terms, and notes. Choose from clean, professional invoice templates.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Can I create invoices in different currencies?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Yes! We support over 170 currencies including USD, EUR, GBP, AUD, CAD, INR, MYR, and SGD for global freelancers and businesses.'
        }
      }
    ]
  };

  return (
    <div className="flex flex-col">
      <SEO
        title="Quick Free Invoice Tool — Free Online Invoice Generator & Quotation Maker"
        description="Quick Free Invoice Tool is a fast, 100% free online invoice generator. Create and download professional PDF & Word invoices in 60 seconds with no sign-up required."
        keywords="quick free invoice tool, free invoice generator, online invoice maker, free quotation generator, estimate maker, pdf invoice creator, freelancer billing software, small business invoicing, contractor invoice template, instant invoice generator no sign up, vat gst tax invoice maker, word invoice generator"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {homePageVersion === 'v2' ? (
        <>
          <div className="relative overflow-hidden bg-[#080b1f] print:bg-transparent print:border-none print:p-0 print:m-0">
            {/* Subtle background glow effect */}
            <div className="absolute -top-48 left-[8%] h-[38rem] w-[38rem] rounded-full bg-indigo-500/35 blur-[130px] pointer-events-none"></div>
            <div className="absolute -right-40 top-20 h-[34rem] w-[34rem] rounded-full bg-violet-500/25 blur-[130px] pointer-events-none"></div>
            <div className="absolute bottom-20 left-1/3 h-72 w-72 rounded-full bg-cyan-400/15 blur-[110px] pointer-events-none"></div>
            <div className="absolute inset-0 opacity-[0.18] pointer-events-none [background-image:linear-gradient(rgba(255,255,255,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.12)_1px,transparent_1px)] [background-size:52px_52px]"></div>
            
            <div className="relative container mx-auto px-4 pt-14 pb-7 text-center max-w-3xl print:hidden">
              <Badge className="border-white/15 bg-white/10 text-indigo-100 mb-5 px-3 py-1 font-semibold text-xs tracking-wider uppercase rounded-full backdrop-blur-sm">
                ⚡ Quick Free Invoice Tool (No Sign-Up)
              </Badge>
              <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white tracking-tight leading-[1.06]">
                Quick Free Invoice Tool — Free Online Invoice Generator &amp; Quotation Maker
              </h1>
              <p className="text-base md:text-lg text-slate-300 mt-5 max-w-2xl mx-auto leading-relaxed font-normal">
                Quick Free Invoice Tool lets you design, print, and download clean PDF and Word invoices in under 60 seconds with zero credit card required.
              </p>
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link to="/try-it-out"><Button size="lg" className="w-full bg-white px-7 font-bold text-indigo-700 shadow-xl shadow-indigo-900/40 hover:bg-indigo-50 sm:w-auto">Create free invoice <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
                <Link to="/templates"><Button size="lg" variant="outline" className="w-full border-white/20 bg-white/5 px-7 font-semibold text-white hover:bg-white/15 hover:text-white sm:w-auto">Browse templates</Button></Link>
              </div>
              <p className="mt-4 text-xs font-medium text-slate-400">No account required to start · PDF &amp; Word export · 170+ currencies</p>
              {/* Top Social Share Buttons */}
              <div className="hidden items-center justify-center gap-2 mt-4 flex-wrap">
                <span className="text-xs text-slate-500 font-medium">Share this tool:</span>
                <a
                  href="https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fquickfreeinvoicetool.com%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share QuickFreeInvoice on Facebook"
                  className="px-3 py-1.5 text-xs font-semibold bg-white hover:bg-blue-600 hover:text-white text-slate-700 border border-slate-200 shadow-sm rounded-full transition-all duration-200 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H7.5v-3H10V9.5C10 7.01 11.49 5.6 13.73 5.6c1.07 0 2.19.19 2.19.19v2.41h-1.24c-1.23 0-1.61.77-1.61 1.56V12h2.72l-.43 3H13v6.8c4.56-.93 8-4.96 8-9.8z" />
                  </svg>
                  Facebook
                </a>
                <a
                  href="https://twitter.com/intent/tweet?text=Create%20free%20PDF%20%26%20Word%20invoices%20in%2060s%20with%20QuickFreeInvoice!&url=https%3A%2F%2Fquickfreeinvoicetool.com%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share QuickFreeInvoice on Twitter / X"
                  className="px-3 py-1.5 text-xs font-semibold bg-white hover:bg-indigo-600 hover:text-white text-slate-700 border border-slate-200 shadow-sm rounded-full transition-all duration-200 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  Tweet
                </a>
                <a
                  href="https://www.linkedin.com/sharing/share-offsite/?url=https%3A%2F%2Fquickfreeinvoicetool.com%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share QuickFreeInvoice on LinkedIn"
                  className="px-3 py-1.5 text-xs font-semibold bg-white hover:bg-blue-600 hover:text-white text-slate-700 border border-slate-200 shadow-sm rounded-full transition-all duration-200 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                  </svg>
                  LinkedIn
                </a>
                <a
                  href="https://api.whatsapp.com/send?text=Create%20free%20PDF%20%26%20Word%20invoices%20in%2060s%20with%20QuickFreeInvoice!%20https%3A%2F%2Fquickfreeinvoicetool.com%2F"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Share QuickFreeInvoice on WhatsApp"
                  className="px-3 py-1.5 text-xs font-semibold bg-white hover:bg-emerald-600 hover:text-white text-slate-700 border border-slate-200 shadow-sm rounded-full transition-all duration-200 flex items-center gap-1.5"
                >
                  WhatsApp
                </a>
              </div>
            </div>
            <div className="relative mx-auto max-w-[1440px] px-3 pb-12 md:px-6 md:pb-16">
              <div className="overflow-hidden rounded-[1.75rem] border border-white/15 bg-white shadow-2xl shadow-black/40 ring-1 ring-white/10">
                <AnonymousEditor hideNoticeHeader={true} />
              </div>
            </div>
          </div>

          {/* Statistics Section for V2 */}
          <section className="hidden py-16 bg-white border-b print:hidden">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div className="space-y-2">
                  <div className="text-4xl md:text-5xl font-extrabold text-primary">
                    {stats.invoicesCount.toLocaleString()}+
                  </div>
                  <div className="font-semibold text-slate-800">Invoices Created</div>
                  <p className="text-slate-500 text-sm">And counting every day</p>
                </div>
                <div className="space-y-2">
                  <div className="text-4xl md:text-5xl font-extrabold text-primary">
                    {stats.usersCount.toLocaleString()}+
                  </div>
                  <div className="font-semibold text-slate-800">Active Users</div>
                  <p className="text-slate-500 text-sm">Trust our platform</p>
                </div>
                <div className="space-y-2">
                  <div className="text-4xl md:text-5xl font-extrabold text-primary">
                    {stats.countriesCount.toLocaleString()}+
                  </div>
                  <div className="font-semibold text-slate-800">Countries</div>
                  <p className="text-slate-500 text-sm">Worldwide coverage</p>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        <>
          {/* Hero Section */}
          <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-emerald-500 text-white">
            <div className="absolute inset-0 animated-gradient opacity-90"></div>
        <div className="relative container mx-auto px-4 pt-4 pb-0 md:pt-6 md:pb-0 lg:pt-8 lg:pb-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="space-y-6 lg:col-span-6">
              <h2 className="font-heading text-4xl md:text-6xl font-bold leading-tight">
                Create Professional Invoices & Quotations in Minutes — Free, No Sign-Up
              </h2>
              <p className="text-xl text-indigo-100">
                Simple, powerful, and completely free generator. Instantly convert estimates to invoices,
                support multiple global taxes, and download as PDF or Word. No credit card required.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {user ? (
                  <Link to="/dashboard">
                    <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
                      Go to Dashboard
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/register">
                      <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
                        Create Invoice Free
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                    <Link to="/try-it-out">
                      <Button
                        size="lg"
                        className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold shadow-lg shadow-emerald-500/10 border-none"
                      >
                        Try It Out
                      </Button>
                    </Link>
                  </>
                )}
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-primary transition-colors"
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  See How It Works
                </Button>
              </div>
            </div>
            {SHOW_INTERACTIVE_INVOICE ? (
              <div className="block w-full mt-12 lg:mt-0 lg:max-w-none lg:col-span-6">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 md:p-6 shadow-2xl space-y-4">

                  {/* Switcher headers & circles */}
                  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-4">
                    <span className="text-sm font-semibold text-white">Interactive Theme Preview</span>
                    <div className="flex gap-2">
                      {INVOICE_TEMPLATES.map((tpl) => (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => setTemplateId(tpl.id)}
                          className={`w-6 h-6 rounded-full border-2 p-0 overflow-hidden cursor-pointer transition-transform focus:outline-none appearance-none ${templateId === tpl.id ? 'border-white scale-125' : 'border-transparent hover:scale-110'
                            }`}
                          style={{ backgroundImage: tpl.thumbnailGradient, backgroundSize: 'cover' }}
                          title={tpl.name}
                          aria-label={`Select ${tpl.name} template theme`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Overlapped V-Shape Cards Container */}
                  <div className="relative w-full h-[620px] sm:h-[650px] md:h-[630px] lg:h-[650px] mt-2 select-none">

                    {/* Underneath Card: Quotation (Rotated Counter-Clockwise) */}
                    <div
                      className="absolute left-[1%] top-[8%] w-[92%] rounded-xl overflow-hidden shadow-xl text-left text-gray-900 bg-white transform -rotate-6 origin-center transition-all duration-300 hover:rotate-0 hover:scale-[1.02] hover:z-30 hover:opacity-100 opacity-80 cursor-pointer"
                      style={{
                        ...activeTpl.backgroundStyle,
                        zIndex: 10
                      }}
                    >
                      <div className="p-5 md:p-6 space-y-4">
                        {/* Header */}
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 space-y-1">
                            <input
                              type="text"
                              value={quoteBusinessName}
                              onChange={(e) => setQuoteBusinessName(e.target.value)}
                              className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full font-bold text-lg md:text-xl p-0"
                              style={{ color: activeTpl.bodyColor }}
                              placeholder="Your Business"
                            />
                            <textarea
                              value={quoteBusinessAddress}
                              onChange={(e) => setQuoteBusinessAddress(e.target.value)}
                              className="bg-transparent border border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full text-[10px] p-0 resize-none h-10"
                              style={{ color: activeTpl.labelColor }}
                              placeholder="Business Address"
                            />
                          </div>
                          <div className="text-right space-y-2 shrink-0">
                            <div className="text-xl md:text-2xl font-bold tracking-wider" style={{ color: activeTpl.headingColor }}>
                              QUOTATION
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <div className="text-[10px]" style={{ color: activeTpl.labelColor }}>
                                Quotation #: {quoteNumber}
                              </div>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-medium border bg-emerald-500/15 text-emerald-500 border-emerald-500/30">
                                Accepted
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Bill To */}
                        <div
                          className="p-2.5 rounded-lg border text-[10px] space-y-1"
                          style={{ background: activeTpl.tableHeaderBg, borderColor: activeTpl.rowBorderColor }}
                        >
                          <span className="font-semibold" style={{ color: activeTpl.headingColor }}>Bill To:</span>
                          <input
                            type="text"
                            value={quoteClientName}
                            onChange={(e) => setQuoteClientName(e.target.value)}
                            className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full font-semibold"
                            style={{ color: activeTpl.bodyColor }}
                            placeholder="Client Name"
                          />
                          <input
                            type="text"
                            value={quoteClientAddress}
                            onChange={(e) => setQuoteClientAddress(e.target.value)}
                            className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full"
                            style={{ color: activeTpl.labelColor }}
                            placeholder="Client Address"
                          />
                        </div>

                        {/* Table of items */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-[10px]">
                            <thead>
                              <tr
                                style={{
                                  background: activeTpl.tableHeaderBg,
                                  borderTop: `1px solid ${activeTpl.rowBorderColor}`,
                                  borderBottom: `1px solid ${activeTpl.rowBorderColor}`,
                                }}
                              >
                                <th className="text-left p-1.5 font-semibold w-14" style={{ color: activeTpl.tableHeaderColor }}>Code</th>
                                <th className="text-left p-1.5 font-semibold" style={{ color: activeTpl.tableHeaderColor }}>Description</th>
                                <th className="text-center p-1.5 font-semibold w-10" style={{ color: activeTpl.tableHeaderColor }}>Unit</th>
                                <th className="text-center p-1.5 font-semibold w-10" style={{ color: activeTpl.tableHeaderColor }}>Qty</th>
                                <th className="text-right p-1.5 font-semibold w-16" style={{ color: activeTpl.tableHeaderColor }}>Rate</th>
                                <th className="text-right p-1.5 font-semibold w-16" style={{ color: activeTpl.tableHeaderColor }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {quoteItems.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: `1px solid ${activeTpl.rowBorderColor}` }}>
                                  <td className="p-1.5">
                                    <input
                                      type="text"
                                      value={item.code}
                                      onChange={(e) => {
                                        const newItems = [...quoteItems];
                                        newItems[idx].code = e.target.value;
                                        setQuoteItems(newItems);
                                      }}
                                      placeholder="e.g. APP-01"
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5">
                                    <input
                                      type="text"
                                      value={item.description}
                                      onChange={(e) => {
                                        const newItems = [...quoteItems];
                                        newItems[idx].description = e.target.value;
                                        setQuoteItems(newItems);
                                      }}
                                      placeholder="Description"
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5 text-center">
                                    <input
                                      type="text"
                                      value={item.unit}
                                      onChange={(e) => {
                                        const newItems = [...quoteItems];
                                        newItems[idx].unit = e.target.value;
                                        setQuoteItems(newItems);
                                      }}
                                      placeholder="pcs"
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full text-center"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5 text-center">
                                    <input
                                      type="number"
                                      value={item.quantity}
                                      onChange={(e) => {
                                        const newItems = [...quoteItems];
                                        newItems[idx].quantity = e.target.value as any;
                                        setQuoteItems(newItems);
                                      }}
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full text-center"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5 text-right">
                                    <input
                                      type="number"
                                      value={item.rate}
                                      onChange={(e) => {
                                        const newItems = [...quoteItems];
                                        newItems[idx].rate = e.target.value as any;
                                        setQuoteItems(newItems);
                                      }}
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full text-right"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5 text-right font-medium" style={{ color: activeTpl.bodyColor }}>
                                    {formatUSD((Number(item.quantity) || 0) * (Number(item.rate) || 0))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Add Item Trigger */}
                        <div className="flex justify-between items-center text-[10px] font-semibold">
                          {quoteItems.length < 3 ? (
                            <button
                              onClick={() => setQuoteItems([...quoteItems, { code: 'NEW-01', description: 'New Line Item', quantity: 1, rate: 100, unit: 'pcs' }])}
                              className="hover:underline"
                              style={{ color: activeTpl.headingColor }}
                            >
                              + Add Line Item
                            </button>
                          ) : (
                            <span className="text-gray-400 cursor-not-allowed">
                              + Add Line Item (Max 3 reached)
                            </span>
                          )}
                          {quoteItems.length > 1 && (
                            <button
                              onClick={() => setQuoteItems(quoteItems.slice(0, -1))}
                              className="text-red-500 hover:underline"
                            >
                              Remove Item
                            </button>
                          )}
                        </div>

                        {/* Totals */}
                        <div className="flex justify-end">
                          <div className="w-44 space-y-1 text-[10px] rounded-lg p-2.5 text-left" style={{ background: activeTpl.totalBg }}>
                            <div className="flex justify-between">
                              <span style={{ color: activeTpl.labelColor }}>Subtotal:</span>
                              <span className="font-medium" style={{ color: activeTpl.bodyColor }}>{formatUSD(quoteSubtotal)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span style={{ color: activeTpl.labelColor }}>VAT (10%):</span>
                              <span className="font-medium" style={{ color: activeTpl.bodyColor }}>{formatUSD(quoteTax)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span style={{ color: activeTpl.labelColor }}>Discount (5%):</span>
                              <span className="font-medium" style={{ color: activeTpl.bodyColor }}>-{formatUSD(quoteDiscount)}</span>
                            </div>
                            <div
                              className="flex justify-between pt-1.5 font-bold text-xs"
                              style={{ borderTop: `1px solid ${activeTpl.headingColor}`, color: activeTpl.headingColor }}
                            >
                              <span>Total:</span>
                              <span>{formatUSD(quoteTotal)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Dummy download/print controls */}
                        <div className="flex justify-end gap-2 border-t pt-3.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (user) {
                                navigate('/dashboard');
                              } else {
                                setShowAuthModal(true);
                              }
                            }}
                            className="text-[10px] h-7 px-2"
                          >
                            Print Quote
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              if (user) {
                                navigate('/dashboard');
                              } else {
                                setShowAuthModal(true);
                              }
                            }}
                            className="text-[10px] h-7 px-2"
                          >
                            Download PDF
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Foreground Top Card: Invoice (Rotated Clockwise) */}
                    <div
                      className="absolute right-[1%] top-[2%] w-[92%] rounded-xl overflow-hidden shadow-2xl text-left text-gray-900 bg-white transform rotate-3 origin-center transition-all duration-300 hover:rotate-0 hover:scale-[1.02] hover:z-30 cursor-pointer"
                      style={{
                        ...activeTpl.backgroundStyle,
                        zIndex: 20
                      }}
                    >
                      <div className="p-5 md:p-6 space-y-4">
                        {/* Header */}
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1 space-y-1">
                            <input
                              type="text"
                              value={businessName}
                              onChange={(e) => setBusinessName(e.target.value)}
                              className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full font-bold text-lg md:text-xl p-0"
                              style={{ color: activeTpl.bodyColor }}
                              placeholder="Your Business"
                            />
                            <textarea
                              value={businessAddress}
                              onChange={(e) => setBusinessAddress(e.target.value)}
                              className="bg-transparent border border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full text-[10px] p-0 resize-none h-10"
                              style={{ color: activeTpl.labelColor }}
                              placeholder="Business Address"
                            />
                          </div>
                          <div className="text-right space-y-2 shrink-0">
                            <div className="text-xl md:text-2xl font-bold tracking-wider" style={{ color: activeTpl.headingColor }}>
                              INVOICE
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <div className="text-[10px]" style={{ color: activeTpl.labelColor }}>
                                Invoice #: {invoiceNumber}
                              </div>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-medium border bg-blue-500/15 text-blue-400 border-blue-500/30">
                                Sent
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Bill To */}
                        <div
                          className="p-2.5 rounded-lg border text-[10px] space-y-1"
                          style={{ background: activeTpl.tableHeaderBg, borderColor: activeTpl.rowBorderColor }}
                        >
                          <span className="font-semibold" style={{ color: activeTpl.headingColor }}>Bill To:</span>
                          <input
                            type="text"
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                            className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full font-semibold"
                            style={{ color: activeTpl.bodyColor }}
                            placeholder="Client Name"
                          />
                          <input
                            type="text"
                            value={clientAddress}
                            onChange={(e) => setClientAddress(e.target.value)}
                            className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full"
                            style={{ color: activeTpl.labelColor }}
                            placeholder="Client Address"
                          />
                        </div>

                        {/* Table of items */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-[10px]">
                            <thead>
                              <tr
                                style={{
                                  background: activeTpl.tableHeaderBg,
                                  borderTop: `1px solid ${activeTpl.rowBorderColor}`,
                                  borderBottom: `1px solid ${activeTpl.rowBorderColor}`,
                                }}
                              >
                                <th className="text-left p-1.5 font-semibold w-14" style={{ color: activeTpl.tableHeaderColor }}>Code</th>
                                <th className="text-left p-1.5 font-semibold" style={{ color: activeTpl.tableHeaderColor }}>Description</th>
                                <th className="text-center p-1.5 font-semibold w-10" style={{ color: activeTpl.tableHeaderColor }}>Unit</th>
                                <th className="text-center p-1.5 font-semibold w-10" style={{ color: activeTpl.tableHeaderColor }}>Qty</th>
                                <th className="text-right p-1.5 font-semibold w-16" style={{ color: activeTpl.tableHeaderColor }}>Rate</th>
                                <th className="text-right p-1.5 font-semibold w-16" style={{ color: activeTpl.tableHeaderColor }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item, idx) => (
                                <tr key={idx} style={{ borderBottom: `1px solid ${activeTpl.rowBorderColor}` }}>
                                  <td className="p-1.5">
                                    <input
                                      type="text"
                                      value={item.code}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[idx].code = e.target.value;
                                        setItems(newItems);
                                      }}
                                      placeholder="e.g. ITEM-01"
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5">
                                    <input
                                      type="text"
                                      value={item.description}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[idx].description = e.target.value;
                                        setItems(newItems);
                                      }}
                                      placeholder="Description"
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5 text-center">
                                    <input
                                      type="text"
                                      value={item.unit}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[idx].unit = e.target.value;
                                        setItems(newItems);
                                      }}
                                      placeholder="pcs"
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full text-center"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5 text-center">
                                    <input
                                      type="number"
                                      value={item.quantity}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[idx].quantity = e.target.value as any;
                                        setItems(newItems);
                                      }}
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full text-center"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5 text-right">
                                    <input
                                      type="number"
                                      value={item.rate}
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        newItems[idx].rate = e.target.value as any;
                                        setItems(newItems);
                                      }}
                                      className="bg-transparent border-b border-transparent hover:border-gray-400 focus:border-primary focus:outline-none w-full text-right"
                                      style={{ color: activeTpl.bodyColor }}
                                    />
                                  </td>
                                  <td className="p-1.5 text-right font-medium" style={{ color: activeTpl.bodyColor }}>
                                    {formatUSD((Number(item.quantity) || 0) * (Number(item.rate) || 0))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Add Item Trigger */}
                        <div className="flex justify-between items-center text-[10px] font-semibold">
                          {items.length < 3 ? (
                            <button
                              onClick={() => setItems([...items, { code: 'NEW-01', description: 'New Line Item', quantity: 1, rate: 100, unit: 'pcs' }])}
                              className="hover:underline"
                              style={{ color: activeTpl.headingColor }}
                            >
                              + Add Line Item
                            </button>
                          ) : (
                            <span className="text-gray-400 cursor-not-allowed">
                              + Add Line Item (Max 3 reached)
                            </span>
                          )}
                          {items.length > 1 && (
                            <button
                              onClick={() => setItems(items.slice(0, -1))}
                              className="text-red-500 hover:underline"
                            >
                              Remove Item
                            </button>
                          )}
                        </div>

                        {/* Totals */}
                        <div className="flex justify-end">
                          <div className="w-44 space-y-1 text-[10px] rounded-lg p-2.5 text-left" style={{ background: activeTpl.totalBg }}>
                            <div className="flex justify-between">
                              <span style={{ color: activeTpl.labelColor }}>Subtotal:</span>
                              <span className="font-medium" style={{ color: activeTpl.bodyColor }}>{formatUSD(subtotal)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span style={{ color: activeTpl.labelColor }}>VAT (10%):</span>
                              <span className="font-medium" style={{ color: activeTpl.bodyColor }}>{formatUSD(tax)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span style={{ color: activeTpl.labelColor }}>Discount (5%):</span>
                              <span className="font-medium" style={{ color: activeTpl.bodyColor }}>-{formatUSD(discount)}</span>
                            </div>
                            <div
                              className="flex justify-between pt-1.5 font-bold text-xs"
                              style={{ borderTop: `1px solid ${activeTpl.headingColor}`, color: activeTpl.headingColor }}
                            >
                              <span>Total:</span>
                              <span>{formatUSD(total)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Dummy download/print controls */}
                        <div className="flex justify-end gap-2 border-t pt-3.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (user) {
                                navigate('/dashboard');
                              } else {
                                setShowAuthModal(true);
                              }
                            }}
                            className="text-[10px] h-7 px-2"
                          >
                            Print Invoice
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              if (user) {
                                navigate('/dashboard');
                              } else {
                                setShowAuthModal(true);
                              }
                            }}
                            className="text-[10px] h-7 px-2"
                          >
                            Download PDF
                          </Button>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            ) : (
              <div className="block w-full mt-12 lg:mt-0 max-w-xl lg:max-w-none mx-auto flex items-end justify-center lg:col-span-6 lg:self-end">
                <img
                  src="/home_hero_invoice.png"
                  alt="QuickFreeInvoice Creator Hero"
                  width="580"
                  height="760"
                  className="w-full max-h-[760px] object-contain drop-shadow-2xl hover:scale-[1.01] transition-transform duration-500"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-white border-y">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                {stats.invoicesCount > 0 ? stats.invoicesCount.toLocaleString() : '...'}{stats.invoicesCount > 0 ? '+' : ''}
              </div>
              <div className="text-lg font-semibold text-gray-900 mb-1">
                Invoices Created
              </div>
              <div className="text-sm text-muted-foreground">
                And counting every day
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                {stats.usersCount > 0 ? stats.usersCount.toLocaleString() : '...'}{stats.usersCount > 0 ? '+' : ''}
              </div>
              <div className="text-lg font-semibold text-gray-900 mb-1">
                Active Users
              </div>
              <div className="text-sm text-muted-foreground">
                Trust our platform
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                {stats.countriesCount > 0 ? stats.countriesCount.toLocaleString() : '...'}{stats.countriesCount > 0 ? '+' : ''}
              </div>
              <div className="text-lg font-semibold text-gray-900 mb-1">
                Countries
              </div>
              <div className="text-sm text-muted-foreground">
                Worldwide coverage
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )}

      {/* Features Section */}
      <section className="py-20 bg-gray-50 print:hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Free Invoice Generator Features — Everything You Need to Bill Clients Professionally
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful features to help you create professional invoices quickly and easily
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-indigo-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <Calculator className="h-6 w-6 text-indigo-600" />
                </div>
                <CardTitle className="text-xl font-bold">Smart Calculations</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Automatic math for taxes and discounts. Real-time subtotal, discount rates, and grand total calculations.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-teal-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <Download className="h-6 w-6 text-teal-600" />
                </div>
                <CardTitle className="text-xl font-bold">Multiple Export Formats</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Export and download your documents as high-quality PDF files or editable MS Word documents in seconds.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-purple-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <Percent className="h-6 w-6 text-purple-600" />
                </div>
                <CardTitle className="text-xl font-bold">Predefined Tax Presets</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Configure tax profiles (VAT, GST, SST) with custom labels and rates in your profile to auto-fill document builder forms instantly.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-emerald-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <Share2 className="h-6 w-6 text-emerald-600" />
                </div>
                <CardTitle className="text-xl font-bold">Unique Client Sharing Links</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Generate public, secure URLs for your documents. Clients can review, download, or print invoices directly online.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-blue-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <Plus className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-xl font-bold">Dashboard Quick-Add Dialogs</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Add new client contacts, business profiles, and catalog items inline on the fly without leaving your draft editor.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-cyan-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-cyan-600" />
                </div>
                <CardTitle className="text-xl font-bold">Multiple Business Profiles</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Switch between multiple business profiles instantly. Each profile maintains its own settings, logos, and default currencies.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-green-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
                <CardTitle className="text-xl font-bold">Secure Cloud Storage</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Securely save your invoices, client directories, and billing statistics in the cloud. Access them safely anytime.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-amber-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-amber-600" />
                </div>
                <CardTitle className="text-xl font-bold">One-Click Conversion</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Save time by converting generated quotations and estimates directly into final invoices with a single click.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-rose-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-rose-600" />
                </div>
                <CardTitle className="text-xl font-bold">Interactive Analytics</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Track payments, outstanding balances, overdue bills, monthly timelines, and client rankings with visual dashboard charts.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-indigo-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <Eye className="h-6 w-6 text-indigo-600" />
                </div>
                <CardTitle className="text-xl font-bold">Client View Tracking</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Know exactly when and how many times your client opened their invoice. Real-time read receipts with browser and device details — so you always know when to follow up.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="transition-all duration-300 hover:shadow-md hover:-translate-y-1 border-gray-200/60 bg-white">
              <CardHeader>
                <div className="bg-violet-50 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                  <CreditCard className="h-6 w-6 text-violet-600" />
                </div>
                <CardTitle className="text-xl font-bold">Direct Online Payment Links</CardTitle>
                <CardDescription className="text-slate-600 mt-2 text-sm leading-relaxed">
                  Attach your Stripe or PayPal payment link directly to any invoice. Clients see prominent "Pay Now" buttons on their shared invoice view — reducing payment delays.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-white print:hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              How to Create a Free Invoice Online in 3 Simple Steps
            </h2>
            <p className="text-lg text-muted-foreground">
              Create your first invoice in just 3 simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center space-y-4">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto text-2xl font-bold text-primary">
                1
              </div>
              <h3 className="font-heading text-xl font-semibold">Create</h3>
              <p className="text-muted-foreground">
                Fill in your business details, customer information, and invoice items
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto text-2xl font-bold text-primary">
                2
              </div>
              <h3 className="font-heading text-xl font-semibold">Customize</h3>
              <p className="text-muted-foreground">
                Add taxes, discounts, notes, and payment terms
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto text-2xl font-bold text-primary">
                3
              </div>
              <h3 className="font-heading text-xl font-semibold">Download</h3>
              <p className="text-muted-foreground">
                Export as PDF or Word and send to your clients instantly
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Advertisement Banner */}
      <section className="py-8 bg-slate-50 border-t border-b border-gray-200/60 print:hidden">
        <div className="container mx-auto px-4 flex flex-col items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Sponsored</span>
          <div className="max-w-full overflow-hidden rounded-lg shadow-sm border border-gray-200/85 bg-white p-2">
            <a href="https://easychurchmanagement.com/?ref=blafli" target="_blank" rel="noopener noreferrer" className="block">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/easychurchmanagement.firebasestorage.app/o/creative_assets%2Fimg_1782613220397_lkrolnz.png?alt=media&token=fa4b3530-7f2e-4337-bd2e-fe3ca3c93e63" 
                alt="Easy Church Management" 
                width="728" 
                height="90" 
                className="max-w-full h-auto mx-auto border-none block"
                style={{ border: 'none' }}
              />
            </a>
          </div>
        </div>
      </section>

      {/* Pro Features Showcase Section */}
      <section className="py-20 bg-slate-900 text-white overflow-hidden relative print:hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,var(--tw-gradient-stops))] from-indigo-900/40 via-slate-900 to-slate-950 -z-0"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">

            {/* Left Column: Visual Mockup */}
            <div className="lg:col-span-5 relative group self-center">
              <div className="absolute -inset-1.5 bg-gradient-to-r from-indigo-500 via-purple-600 to-emerald-500 rounded-2xl blur-lg opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-tilt"></div>
              <div className="relative bg-slate-950/80 rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
                <img
                  src="/pro_analytics_dashboard_preview.png?v=4"
                  alt="Pro Analytics Dashboard Preview"
                  width="600"
                  height="400"
                  className="w-full h-auto object-cover transform hover:scale-[1.02] transition-transform duration-700"
                />
              </div>
            </div>

            {/* Right Column: Feature Explanations */}
            <div className="lg:col-span-7 space-y-8">
              <div className="space-y-3">
                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-bold px-3 py-1 flex items-center gap-1.5 w-fit border-amber-200">
                  <Crown className="h-4.5 w-4.5 text-amber-700" />
                  PREMIUM BUSINESS UPGRADES
                </Badge>
                <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">
                  Supercharge Your Invoicing with Pro — Advanced Tools for Freelancers &amp; Businesses
                </h2>
                <p className="text-base text-slate-300 max-w-xl">
                  Unlock advanced tools designed to help freelancers and small businesses save time, track cash flows, and secure billing data.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Feature 1 */}
                <div className="group relative bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.05] hover:border-indigo-500/30 rounded-xl p-4 transition-all duration-300 shadow-md flex flex-col gap-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-indigo-500/10 border border-indigo-500/20 group-hover:scale-105 group-hover:bg-indigo-500/20 transition-all duration-300 flex items-center justify-center shrink-0 text-indigo-400">
                      <TrendingUp className="h-4.5 w-4.5" />
                    </div>
                    <h3 className="font-semibold text-slate-100 text-sm group-hover:text-white transition-colors">Analytics Dashboard</h3>
                  </div>
                  <p className="text-slate-400 text-[11.5px] leading-relaxed group-hover:text-slate-300 transition-colors">
                    Monitor collections, outstanding balances, and client rankings in real time with dynamic charts.
                  </p>
                </div>

                {/* Feature 2 */}
                <div className="group relative bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.05] hover:border-sky-500/30 rounded-xl p-4 transition-all duration-300 shadow-md flex flex-col gap-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-sky-500/10 border border-sky-500/20 group-hover:scale-105 group-hover:bg-sky-500/20 transition-all duration-300 flex items-center justify-center shrink-0 text-sky-400">
                      <Eye className="h-4.5 w-4.5" />
                    </div>
                    <h3 className="font-semibold text-slate-100 text-sm group-hover:text-white transition-colors">Manage Client Billing & Tracking Easily</h3>
                  </div>
                  <p className="text-slate-400 text-[11.5px] leading-relaxed group-hover:text-slate-300 transition-colors">
                    Get real-time read receipts with view timestamps, browser, and device details to know when to follow up.
                  </p>
                </div>

                {/* Feature 3 */}
                <div className="group relative bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.05] hover:border-purple-500/30 rounded-xl p-4 transition-all duration-300 shadow-md flex flex-col gap-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-purple-500/10 border border-purple-500/20 group-hover:scale-105 group-hover:bg-purple-500/20 transition-all duration-300 flex items-center justify-center shrink-0 text-purple-400">
                      <Users className="h-4.5 w-4.5" />
                    </div>
                    <h3 className="font-semibold text-slate-100 text-sm group-hover:text-white transition-colors">Create Tax-Compliant Invoices</h3>
                  </div>
                  <p className="text-slate-400 text-[11.5px] leading-relaxed group-hover:text-slate-300 transition-colors">
                    Save client contacts, business details, catalogs, and custom tax rules for instant auto-filling.
                  </p>
                </div>

                {/* Feature 4 */}
                <div className="group relative bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.05] hover:border-emerald-500/30 rounded-xl p-4 transition-all duration-300 shadow-md flex flex-col gap-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 group-hover:scale-105 group-hover:bg-emerald-500/20 transition-all duration-300 flex items-center justify-center shrink-0 text-emerald-400">
                      <Share2 className="h-4.5 w-4.5" />
                    </div>
                    <h3 className="font-semibold text-slate-100 text-sm group-hover:text-white transition-colors">Secure Client Sharing</h3>
                  </div>
                  <p className="text-slate-400 text-[11.5px] leading-relaxed group-hover:text-slate-300 transition-colors">
                    Share professional documents via unique links. Clients can view, print, or download instantly.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom part wrapping around the mockup */}
          <div className="mt-8 pt-8 border-t border-white/5 space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Feature 5 */}
              <div className="group relative bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.05] hover:border-blue-500/30 rounded-xl p-4 transition-all duration-300 shadow-md flex flex-col gap-2.5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 group-hover:scale-105 group-hover:bg-blue-500/20 transition-all duration-300 flex items-center justify-center shrink-0 text-blue-400">
                    <Plus className="h-4.5 w-4.5" />
                  </div>
                  <h3 className="font-semibold text-slate-100 text-sm group-hover:text-white transition-colors">Dashboard Quick-Add</h3>
                </div>
                <p className="text-slate-400 text-[11.5px] leading-relaxed group-hover:text-slate-300 transition-colors">
                  Create and save business profiles, client details, and products inline directly inside invoice builders.
                </p>
              </div>

              {/* Feature 6 */}
              <div className="group relative bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.05] hover:border-cyan-500/30 rounded-xl p-4 transition-all duration-300 shadow-md flex flex-col gap-2.5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/20 group-hover:scale-105 group-hover:bg-cyan-500/20 transition-all duration-300 flex items-center justify-center shrink-0 text-cyan-400">
                    <Download className="h-4.5 w-4.5" />
                  </div>
                  <h3 className="font-semibold text-slate-100 text-sm group-hover:text-white transition-colors">Free PDF & Word Invoice Generator</h3>
                </div>
                <p className="text-slate-400 text-[11.5px] leading-relaxed group-hover:text-slate-300 transition-colors">
                  Download and share high-quality PDF and editable MS Word templates without any daily limits.
                </p>
              </div>

              {/* Feature 7 */}
              <div className="group relative bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.05] hover:border-amber-500/30 rounded-xl p-4 transition-all duration-300 shadow-md flex flex-col gap-2.5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/20 group-hover:scale-105 group-hover:bg-amber-500/20 transition-all duration-300 flex items-center justify-center shrink-0 text-amber-400">
                    <Shield className="h-4.5 w-4.5" />
                  </div>
                  <h3 className="font-semibold text-slate-100 text-sm group-hover:text-white transition-colors">Two-Factor Auth (2FA)</h3>
                </div>
                <p className="text-slate-400 text-[11.5px] leading-relaxed group-hover:text-slate-300 transition-colors">
                  Secure your billing details, catalogs, and statistics. Toggle MFA settings right from your profile.
                </p>
              </div>

              {/* Feature 8 */}
              <div className="group relative bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.05] hover:border-yellow-500/30 rounded-xl p-4 transition-all duration-300 shadow-md flex flex-col gap-2.5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-yellow-500/10 border border-yellow-500/20 group-hover:scale-105 group-hover:bg-yellow-500/20 transition-all duration-300 flex items-center justify-center shrink-0 text-yellow-400">
                    <ImageIcon className="h-4.5 w-4.5" />
                  </div>
                  <h3 className="font-semibold text-slate-100 text-sm group-hover:text-white transition-colors">Business Branding</h3>
                </div>
                <p className="text-slate-400 text-[11.5px] leading-relaxed group-hover:text-slate-300 transition-colors">
                  Upload your business logo and apply it directly onto your screen views, PDF, and Word documents.
                </p>
              </div>

              {/* Feature 9 */}
              <div className="group lg:col-span-2 relative bg-gradient-to-r from-violet-500/5 to-indigo-500/5 hover:from-violet-500/10 hover:to-indigo-500/10 border border-violet-500/10 hover:border-violet-500/30 rounded-xl p-4 transition-all duration-300 shadow-md flex flex-col gap-2.5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-500/20 group-hover:scale-105 group-hover:bg-violet-500/20 transition-all duration-300 flex items-center justify-center shrink-0 text-violet-400">
                    <CreditCard className="h-4.5 w-4.5" />
                  </div>
                  <h3 className="font-semibold text-slate-100 text-sm group-hover:text-white transition-colors">Stripe & PayPal Integration</h3>
                </div>
                <p className="text-slate-400 text-[11.5px] leading-relaxed group-hover:text-slate-300 transition-colors">
                  Embed Stripe or PayPal.me links directly into documents. Clients can pay instantly via one-click checkout buttons.
                </p>
              </div>
            </div>

            <div className="flex justify-center pt-2">
              <Link to="/subscription">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold border-0 shadow-lg px-8 py-6 rounded-xl w-full sm:w-auto">
                  Upgrade to Pro Plan
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50 print:hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Free Invoice Generator Pricing — Start Free, Upgrade Anytime
            </h2>
            <p className="text-lg text-muted-foreground">
              Start free, upgrade when you need more
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Free</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$0</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Unlimited invoice & quotation downloads</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Manual business entry each time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Manual customer entry each time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Manual item entry each time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Save unlimited invoices &amp; quotes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Quotations &amp; Estimates (manual)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Convert quotes to invoices instantly</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>PDF export</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Standard support</span>
                  </li>
                </ul>
                <Link to="/register" className="block">
                  <Button variant="outline" className="w-full">
                    Get Started Free
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Pro Plan */}
            <Card className="border-2 border-primary relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                  <Crown className="h-4 w-4" />
                  Most Popular
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  Pro
                  <Crown className="h-5 w-5 text-yellow-500" />
                </CardTitle>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-xl text-muted-foreground line-through font-semibold">$15</span>
                  <span className="text-4xl font-bold text-slate-900">$5</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Unlimited invoice & quotation downloads</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Save unlimited business profiles</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Business logo upload & custom branding</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Save unlimited customer profiles</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Save unlimited inventory items & services (search & auto-fill)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Save unlimited invoices &amp; quotes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Quotations &amp; Estimates (with profile auto-fill)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Convert quotes to invoices with auto-fill</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">PDF Export</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Word Document Export</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Predefined tax rates setup (auto-fill)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Unique invoice & quotation sharing links</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Client view tracking (read receipts)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Direct online payment links (Stripe & PayPal)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="font-medium">Priority email support</span>
                  </li>
                </ul>
                <Link to="/register" className="block">
                  <Button className="w-full">
                    Upgrade to Pro Plan
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-white print:hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Use Cases for Quick Free Invoice Tool — Who Uses It?
            </h2>
            <p className="text-lg text-muted-foreground">
              Trusted by freelancers, small businesses, and enterprises worldwide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Freelancers</CardTitle>
                <CardDescription>
                  Create professional invoices for your clients in minutes. Track payments and manage multiple projects effortlessly.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Small Businesses</CardTitle>
                <CardDescription>
                  Streamline your billing process with unlimited invoices. Save time and get paid faster with professional documentation.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Consultants</CardTitle>
                <CardDescription>
                  Bill clients for hourly work or project-based fees. Add detailed line items and custom tax calculations.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Service Providers</CardTitle>
                <CardDescription>
                  Generate invoices for services rendered. Support for deposits, partial payments, and payment terms.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Content-Rich SEO Knowledge Hub & Guide Section */}
      <section className="py-20 bg-white border-b print:hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs font-bold text-primary tracking-widest uppercase bg-primary/10 px-3 py-1 rounded-full">
              Complete Billing Solution
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
              Free Online Invoice &amp; Quotation Creator — Complete Guide for Modern Businesses
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed">
              QuickFreeInvoice helps freelancers, contractors, agencies, and small businesses create, customize, and export professional billing documents in seconds.
            </p>
          </div>

          {/* 4-Step How It Works Grid */}
          <div className="mb-20">
            <h3 className="text-2xl font-bold text-slate-900 text-center mb-10 font-heading">
              How to Create Your Free Invoice in 4 Simple Steps
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-6 relative hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-full bg-primary text-white font-bold flex items-center justify-center text-lg mb-4">
                  1
                </div>
                <h4 className="font-bold text-lg text-slate-900 mb-2">Choose Template</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Select from clean, modern invoice layouts tailored for maximum legibility and client trust.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-6 relative hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-full bg-primary text-white font-bold flex items-center justify-center text-lg mb-4">
                  2
                </div>
                <h4 className="font-bold text-lg text-slate-900 mb-2">Add Business Details</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Enter your business name, contact info, logo, client details, and itemized billing items.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-6 relative hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-full bg-primary text-white font-bold flex items-center justify-center text-lg mb-4">
                  3
                </div>
                <h4 className="font-bold text-lg text-slate-900 mb-2">Set Taxes &amp; Currency</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Choose from 170+ global currencies. Add line-item discounts, VAT, GST, or local sales taxes automatically.
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-6 relative hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-full bg-primary text-white font-bold flex items-center justify-center text-lg mb-4">
                  4
                </div>
                <h4 className="font-bold text-lg text-slate-900 mb-2">Download &amp; Print</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Export crisp PDF or editable Word (.docx) documents ready for printing or instant email sending.
                </p>
              </div>
            </div>
          </div>

          {/* Industry Use Cases Grid */}
          <div className="mb-20">
            <div className="text-center max-w-2xl mx-auto mb-12 space-y-2">
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 font-heading">
                Use Cases for Quick Free Invoice Tool
              </h3>
              <p className="text-slate-600 text-sm md:text-base">
                Whether you bill hourly or by project milestone, Quick Free Invoice Tool flexes to fit your exact workflow.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 p-6 rounded-2xl space-y-3">
                <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center font-bold text-xl">
                  💻
                </div>
                <h4 className="font-bold text-xl text-slate-900">Freelancers &amp; Tech Consultants</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Bill clients accurately for software development, graphic design, content writing, or digital marketing. Support for Net 15, Net 30, and milestone payment schedules.
                </p>
              </div>

              <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 p-6 rounded-2xl space-y-3">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center font-bold text-xl">
                  🛠️
                </div>
                <h4 className="font-bold text-xl text-slate-900">Contractors &amp; Service Trades</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Itemize materials, equipment rentals, and labor hours on clean commercial invoices. Convert initial price estimates into final billing invoices with a single click.
                </p>
              </div>

              <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 p-6 rounded-2xl space-y-3">
                <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center font-bold text-xl">
                  🌐
                </div>
                <h4 className="font-bold text-xl text-slate-900">Global Agencies &amp; E-Commerce</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Send compliant tax invoices worldwide in over 170 currencies. Automatically compute multi-tiered regional taxes such as VAT, GST, and SST with precision.
                </p>
              </div>
            </div>
          </div>

          {/* Comparison Table for SEO Authority */}
          <div className="mb-20 bg-slate-900 text-white rounded-3xl p-8 md:p-12 shadow-xl">
            <div className="max-w-3xl mx-auto text-center mb-10 space-y-3">
              <h3 className="text-2xl md:text-4xl font-extrabold font-heading text-white">
                Why QuickFreeInvoice Outperforms Traditional Billing Software
              </h3>
              <p className="text-slate-300 text-sm md:text-base">
                Get everything you need to manage billing without monthly subscriptions or mandatory sign-ups.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-slate-700 text-slate-400 font-semibold uppercase text-xs">
                    <th className="py-4 px-4">Feature</th>
                    <th className="py-4 px-4 text-emerald-400 font-bold">QuickFreeInvoice</th>
                    <th className="py-4 px-4">Traditional Software</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 text-slate-200">
                  <tr>
                    <td className="py-4 px-4 font-medium">Instant PDF &amp; Word Generation</td>
                    <td className="py-4 px-4 text-emerald-400 font-bold">✓ Included (100% Free)</td>
                    <td className="py-4 px-4 text-slate-400">Limited / PDF only</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4 font-medium">No Mandatory Account Creation</td>
                    <td className="py-4 px-4 text-emerald-400 font-bold">✓ Instant Builder Access</td>
                    <td className="py-4 px-4 text-slate-400">✗ Required Credit Card &amp; Signup</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4 font-medium">Quotation to Invoice Conversion</td>
                    <td className="py-4 px-4 text-emerald-400 font-bold">✓ 1-Click Conversion</td>
                    <td className="py-4 px-4 text-slate-400">Paid add-on module</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4 font-medium">Global Currency Support</td>
                    <td className="py-4 px-4 text-emerald-400 font-bold">✓ 170+ Fiat Currencies</td>
                    <td className="py-4 px-4 text-slate-400">Restricted to tier plans</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-4 font-medium">Client Open &amp; View Tracking</td>
                    <td className="py-4 px-4 text-emerald-400 font-bold">✓ Real-time Read Receipts</td>
                    <td className="py-4 px-4 text-slate-400">Premium plan only</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Featured Invoicing Guides & Blog Links */}
          <div className="bg-slate-50 border border-slate-200/80 rounded-3xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 font-heading">
                  Invoicing Knowledge Hub &amp; Guides
                </h3>
                <p className="text-slate-600 text-sm mt-1">
                  Learn how to structure invoices, set payment terms, and manage client billing effectively.
                </p>
              </div>
              <Link
                to="/blog"
                className="inline-flex items-center text-sm font-bold text-primary hover:text-primary/80 transition-colors gap-1 shrink-0"
              >
                View All Articles &rarr;
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/blog/how-to-create-professional-invoice"
                className="bg-white border border-slate-200 rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all group"
              >
                <div className="text-xs font-semibold text-primary mb-2">Guide</div>
                <h4 className="font-bold text-slate-900 group-hover:text-primary transition-colors line-clamp-2 mb-2">
                  How to Create a Professional Invoice in 5 Easy Steps
                </h4>
                <p className="text-xs text-slate-500 line-clamp-3">
                  Learn the essential elements of creating professional invoices that get you paid faster. Perfect guide for freelancers and small business owners.
                </p>
              </Link>

              <Link
                to="/blog/understanding-invoice-payment-terms"
                className="bg-white border border-slate-200 rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all group"
              >
                <div className="text-xs font-semibold text-primary mb-2">Best Practices</div>
                <h4 className="font-bold text-slate-900 group-hover:text-primary transition-colors line-clamp-2 mb-2">
                  Understanding Invoice Payment Terms: Net 30, Net 15, &amp; More
                </h4>
                <p className="text-xs text-slate-500 line-clamp-3">
                  A comprehensive guide to invoice payment terms. Learn how to choose the right terms for your business and improve cash flow.
                </p>
              </Link>

              <Link
                to="/blog/quotation-vs-invoice-difference"
                className="bg-white border border-slate-200 rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all group"
              >
                <div className="text-xs font-semibold text-primary mb-2">Comparison</div>
                <h4 className="font-bold text-slate-900 group-hover:text-primary transition-colors line-clamp-2 mb-2">
                  Quotation vs Invoice: Key Differences Explained
                </h4>
                <p className="text-xs text-slate-500 line-clamp-3">
                  Understand the legal and operational differences between price quotations and formal invoices to streamline client deals.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50 print:hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              What Freelancers &amp; Business Owners Say About Quick Free Invoice Tool
            </h2>
            <p className="text-lg text-muted-foreground">
              Join thousands of satisfied businesses using QuickFreeInvoice
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400">★</span>
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "QuickFreeInvoice has transformed my billing process. I can create professional invoices in under 2 minutes now. The PDF export is perfect!"
                </p>
                <div className="font-semibold">Sarah Johnson</div>
                <div className="text-sm text-muted-foreground">Freelance Designer</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400">★</span>
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "The Pro plan is worth every penny. Saving client and business profiles saves me so much time. Highly recommend for anyone doing regular invoicing."
                </p>
                <div className="font-semibold">Michael Chen</div>
                <div className="text-sm text-muted-foreground">Consulting Agency Owner</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400">★</span>
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">
                  "Best free invoice generator I've found. Clean interface, powerful features, and no hidden fees. The tax calculation features are fantastic!"
                </p>
                <div className="font-semibold">Emily Rodriguez</div>
                <div className="text-sm text-muted-foreground">Small Business Owner</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white print:hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions (FAQ)
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to know about Quick Free Invoice Tool
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            <Card className="h-full transition-all duration-300 hover:shadow-md hover:border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">Is QuickFreeInvoice really free?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes! Our free plan allows you to create, save, and download unlimited invoices and quotations. You can upgrade to Pro to unlock advanced features like saved business, customer, and product profiles, priority support, and Word document exports.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full transition-all duration-300 hover:shadow-md hover:border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">Do I need to install any software?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  No installation required! QuickFreeInvoice is a web-based application that works directly in your browser. Access your invoices from any device, anywhere.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full transition-all duration-300 hover:shadow-md hover:border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">Can I customize my invoices?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Absolutely! Add your business details, logo placeholder, custom line items, taxes, discounts, notes, and payment terms. Support for multiple currencies and tax types including VAT, GST, and SST.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full transition-all duration-300 hover:shadow-md hover:border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">What payment methods do you accept for Pro?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We accept all major credit cards, debit cards, and various payment methods through our secure payment processor Paddle. All transactions are encrypted and secure.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full transition-all duration-300 hover:shadow-md hover:border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">Can I cancel my Pro subscription anytime?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes! You can cancel your Pro subscription at any time with no questions asked. Your Pro features will remain active until the end of your billing period, and we offer a 7-day money-back guarantee.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full transition-all duration-300 hover:shadow-md hover:border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">Is my data secure?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Your data security is our top priority. All data is encrypted in transit and at rest, stored securely in Google Cloud, and we never share your information with third parties.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full transition-all duration-300 hover:shadow-md hover:border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">Can I create invoices in different currencies?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes! We support over 170 currencies including USD, EUR, GBP, INR, MYR, AUD, CAD, and many more. Perfect for international businesses and freelancers.
                </p>
              </CardContent>
            </Card>

            <Card className="h-full transition-all duration-300 hover:shadow-md hover:border-primary/20">
              <CardHeader>
                <CardTitle className="text-xl">How do I get support if I need help?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Email us at support@quickfreeinvoicetool.com for any questions. Pro users get priority email support with faster response times.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Use Quick Free Invoice Tool Section */}
      <section className="py-20 bg-gray-50 print:hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Why Use Quick Free Invoice Tool?
            </h2>
            <p className="text-lg text-muted-foreground">
              The fastest, most user-friendly invoice &amp; quote generator for freelancers and businesses
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center">
                  <Check className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">100% Free to Start</h3>
                <p className="text-muted-foreground">
                  No credit card required. Create, save, and download unlimited invoices and quotations. Upgrade to Pro to unlock automated workflows.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center">
                  <Check className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Lightning Fast</h3>
                <p className="text-muted-foreground">
                  Create professional invoices in under 2 minutes. Intuitive interface designed for speed and efficiency.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center">
                  <Check className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Professional Templates</h3>
                <p className="text-muted-foreground">
                  Clean, modern invoice designs that look professional and make great impressions on clients.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center">
                  <Check className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Cloud Storage</h3>
                <p className="text-muted-foreground">
                  Access your invoices from any device. Never lose an invoice again with secure cloud backup.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="bg-red-100 w-12 h-12 rounded-lg flex items-center justify-center">
                  <Check className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Advanced Tax Support</h3>
                <p className="text-muted-foreground">
                  Support for VAT, GST, SST, and custom taxes. Line-level and global tax calculations with automatic totals.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="bg-indigo-100 w-12 h-12 rounded-lg flex items-center justify-center">
                  <Check className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Multi-Currency</h3>
                <p className="text-muted-foreground">
                  Support for 170+ currencies worldwide. Perfect for international businesses and global freelancers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-secondary text-white print:hidden">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Start Creating Free Professional Invoices Today — No Sign-Up Required
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses using QuickFreeInvoice to manage their billing
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Sign Up Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Auth Modal Trigger for non-logged-in homepage visitors */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-xl shadow-2xl p-6 md:p-8 max-w-md w-full text-center space-y-6 animate-in zoom-in-95 duration-200 text-gray-900">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
              <Crown className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Download Your Invoice</h2>
              <p className="text-gray-600 mt-2">
                Create a free account to download and print professional invoices with custom styling templates.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/register">
                <Button className="w-full" size="lg" aria-label="Create Free Account">
                  Create Free Account
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full" size="lg">
                  Log In
                </Button>
              </Link>
            </div>
            <button
              onClick={() => setShowAuthModal(false)}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
